/**
 * The one place the console talks to the API (T5.1).
 *
 * Every request goes out from here and every response is unwrapped here, for
 * the same reason `lib/respond.ts` is the only place the server writes one: the
 * moment two call sites disagree about the envelope, each screen starts
 * sniffing the shape it got, and the error path — the one nobody exercises by
 * hand — is where they disagree.
 *
 * The envelope is `{ data, meta? }` on success and `{ error: { code, message,
 * details?, requestId } }` on failure. Callers below this file see the payload
 * or an `ApiRequestError`. They never see `data`.
 */
import {
  isApiError,
  type ApiErrorBody,
  type ApiMeta,
  type ApiResponse,
  type ErrorCode,
} from '@cattle/contracts'

import { CSRF_COOKIE, readCookie } from './cookies'

/** Same-origin in the browser; Vite proxies `/api` to the API in dev. */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

/**
 * A failure that already carries everything a support ticket needs.
 *
 * `requestId` is the field that matters: it is echoed in `X-Request-Id` and in
 * the server's log line, so one id quoted by a trader resolves to exactly one
 * request. A thrown `Error` with only a message throws that away.
 */
export class ApiRequestError extends Error {
  readonly code: ErrorCode
  readonly status: number
  readonly requestId: string
  readonly details?: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiRequestError'
    this.code = body.code
    this.status = status
    this.requestId = body.requestId
    this.details = body.details
  }

  /** Field-level messages from a `VALIDATION_FAILED`, for form binding. */
  get fieldErrors(): Record<string, string[]> | undefined {
    const value = this.details?.fieldErrors
    return typeof value === 'object' && value !== null
      ? (value as Record<string, string[]>)
      : undefined
  }
}

/**
 * A transport failure — no envelope came back at all.
 *
 * Kept distinct from `ApiRequestError` because the two need different words in
 * front of a trader: a refused write is "the server said no", a dead connection
 * is "you are offline". Collapsing them produces the screen that tells someone
 * their data was rejected when in fact their phone lost signal.
 */
export class ApiTransportError extends Error {
  /**
   * `code` and `requestId` exist so this is structurally an `ApiErrorBody` and
   * `ErrorState` can render it without a second error shape. `requestId` is
   * empty on purpose: the request never reached a server, so no server ever
   * assigned it one, and inventing one would put an id in a support ticket
   * that resolves to nothing.
   */
  readonly code: ErrorCode = 'INTERNAL'
  readonly requestId = ''

  constructor(cause: unknown) {
    super('The server could not be reached.')
    this.name = 'ApiTransportError'
    this.cause = cause
  }
}

/** What a screen actually catches. Both members satisfy `ApiErrorBody`. */
export type ApiError = ApiRequestError | ApiTransportError

export interface ApiResult<T> {
  data: T
  meta?: ApiMeta
}

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE'

interface RequestOptions {
  /** Query string values. `undefined` entries are dropped, not sent as "undefined". */
  query?: Record<string, string | number | boolean | undefined>
  body?: unknown
  signal?: AbortSignal
  /**
   * A scoped Bearer token, used for exactly one thing: a platform actor standing
   * inside a trader's book through P5. The console's own cookie session is
   * deliberately NOT bound to the grant — so an in-book request must carry the
   * grant's token, and every other request must not.
   */
  token?: string
}

/**
 * Get a CSRF token if this browser has not been issued one yet.
 *
 * The API mints `cb_csrf` on any SAFE request and demands it back in a header on
 * every unsafe one (`middleware/csrf.ts`). A console whose very first request is
 * the sign-in POST therefore has no token to echo and is refused — which is what
 * happens on a cold load, since there is nothing to fetch before signing in.
 * One anonymous GET closes that gap; it is part of the double-submit protocol,
 * not a workaround for it.
 */
async function primeCsrfToken(): Promise<string | undefined> {
  const existing = readCookie(CSRF_COOKIE)
  if (existing) return existing
  try {
    await fetch(`${BASE_URL}/health`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    })
  } catch {
    // The unsafe request that follows will fail on its own and say so properly.
  }
  return readCookie(CSRF_COOKIE)
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value))
  }
  const search = params.toString()
  return search ? `${url}?${search}` : url
}

/**
 * Returns the payload AND `meta`, because a paginated list needs the cursor and
 * a client that has to make a second call to find out whether there is a next
 * page has already lost the round trip that pagination was meant to save.
 */
export async function request<T>(
  method: Method,
  path: string,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.token) headers.Authorization = `Bearer ${options.token}`

  // The console authenticates by cookie, so it must echo the double-submit
  // token on every unsafe verb. The mobile app uses Bearer and is exempt —
  // see `middleware/csrf.ts` for why that is not a hole.
  // A grant's Bearer request is exempt: nothing attaches that header ambiently,
  // which is the whole reason CSRF applies to the cookie transport only.
  if (method !== 'GET' && !options.token) {
    const token = await primeCsrfToken()
    if (token) headers['X-CSRF-Token'] = token
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // The session is an httpOnly cookie; without this it is simply not sent.
      credentials: 'include',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (cause) {
    throw new ApiTransportError(cause)
  }

  // 204 is the documented shape for a delete. There is no envelope to parse and
  // asking for one would throw on a perfectly successful request.
  if (response.status === 204) return { data: undefined as T }

  let envelope: ApiResponse<T>
  try {
    envelope = (await response.json()) as ApiResponse<T>
  } catch (cause) {
    // A body that is not JSON means something upstream answered instead of the
    // API — a proxy error page, usually. That is a transport fact, not a
    // business refusal, so it must not surface as a rejected form.
    throw new ApiTransportError(cause)
  }

  if (isApiError(envelope)) throw new ApiRequestError(response.status, envelope.error)

  return { data: envelope.data, meta: envelope.meta }
}

/** The common case: the payload, nothing else. */
export async function get<T>(
  path: string,
  query?: RequestOptions['query'],
  token?: string,
): Promise<T> {
  return (await request<T>('GET', path, { query, token })).data
}

export async function post<T>(path: string, body?: unknown, token?: string): Promise<T> {
  return (await request<T>('POST', path, { body, token })).data
}

export async function patch<T>(path: string, body?: unknown, token?: string): Promise<T> {
  return (await request<T>('PATCH', path, { body, token })).data
}

/** `body` because P8's delete asks for the slug typed back — see the contract. */
export async function del<T>(path: string, body?: unknown, token?: string): Promise<T> {
  return (await request<T>('DELETE', path, { body, token })).data
}

/** Kept separate from `get` so a list keeps its cursor. */
export async function getList<T>(
  path: string,
  query?: RequestOptions['query'],
  token?: string,
): Promise<ApiResult<T>> {
  return request<T>('GET', path, { query, token })
}

/**
 * Whether TanStack Query should retry.
 *
 * A refused request is refused deterministically — retrying a 403 three times
 * produces three audit-log rows and the same answer. Only a transport failure
 * and a 5xx are worth a second attempt.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (error instanceof ApiTransportError) return true
  return error instanceof ApiRequestError && error.status >= 500
}

/**
 * A media fetch that did not come back with bytes.
 *
 * It carries the status because the caller draws a placeholder rather than an
 * error screen, and a placeholder that cannot say whether it is a 404 or a 401
 * is indistinguishable from an animal that was never photographed. `status` is
 * `0` when the request never reached the server at all.
 */
export class MediaFetchError extends Error {
  readonly status: number

  constructor(mediaId: string, status: number) {
    super(`Media ${mediaId} answered ${status}.`)
    this.name = 'MediaFetchError'
    this.status = status
  }
}

/**
 * A protected media variant as a `blob:` URL. `GET /media/:id/:variant` is never
 * public, and an `<img src>` cannot carry a Bearer — so the platform console,
 * reading a book through a grant's scoped token, fetches the bytes itself. The
 * caller owns the URL and must revoke it.
 */
export async function fetchMediaObjectUrl(
  mediaId: string,
  variant: string,
  token: string,
): Promise<string> {
  let response: Response
  try {
    response = await fetch(`${BASE_URL}/media/${mediaId}/${variant}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    })
  } catch {
    throw new MediaFetchError(mediaId, 0)
  }
  if (!response.ok) throw new MediaFetchError(mediaId, response.status)
  return URL.createObjectURL(await response.blob())
}
